import { useMemo } from 'react';
import { api } from '~/trpc-extra/api';
import type { RouterOutputs } from '~/trpc-extra/api';
import { DataTable } from '~/components/tables/common/DataTable';
import { emailColumns } from '~/components/tables/email/emailColumns';
import { useDataTable } from '~/components/tables/common/useDataTable';
import { MailboxQuotaAlert } from '~/components/email/MailboxQuotaAlert';
import { Button, Loader, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useProjectId } from '~/hooks/context/useProjectId';
import Link from 'next/link';

type InboxEmail = RouterOutputs['stablyEmail']['listInbox']['emails'][number];

export function EmailPanel() {
  const projectId = useProjectId();
  const {
    data: inboxData,
    isFetching: isFetchingInbox,
    refetch: refetchInbox,
  } = api.stablyEmail.listInbox.useQuery(
    { projectId: projectId! },
    { refetchOnWindowFocus: false, refetchOnMount: false, retry: false },
  );
  const emailsWithStringId = useMemo(() => {
    return (inboxData?.emails || []).map((email: InboxEmail) => ({
      ...email,
      id: String(email.id),
      metaData: {
        projectId,
      },
    }));
  }, [inboxData?.emails, projectId]);

  const table = useDataTable({ data: emailsWithStringId, columns: emailColumns });

  const [orgName, domain] = inboxData?.emailAddress.split('@') || [];
  const emailAddress = `${orgName}+<your-prefix>@${domain}`;

  return (
    <div className="flex h-full flex-col border-t dark:border-neutral-700">
      {isFetchingInbox ?
        <div className="flex h-full items-center justify-center">
          <Loader size="sm" />
        </div>
      : inboxData ?
        <>
          <div className="flex items-center justify-between p-2">
            <Text className="bg-[var(--mantine-color-body)] font-semibold">{emailAddress}</Text>
            <Button
              leftSection={<IconRefresh size={15} />}
              onClick={() => refetchInbox()}
            >
              Refresh
            </Button>
          </div>
          <MailboxQuotaAlert
            quota={inboxData.quota}
            className="mx-2 mb-2"
          />
          <DataTable
            table={table}
            rowLinkProps={(row) => ({
              href: `/project/${projectId}/mail/${row.original.mailbox}-${row.original.id}`,
              target: '_blank',
            })}
            emptyMessage={<div />}
          />
        </>
      : <div className="flex h-full flex-col items-center justify-center gap-2 bg-[var(--mantine-color-body)] py-6">
          <Text className="font-semibold">No email setup.</Text>
          <Link
            href="https://docs.stably.ai/platform-capabilities/receive-emails"
            target="_blank"
            className="text-blue-500 hover:underline"
          >
            Learn more
          </Link>
        </div>
      }
    </div>
  );
}
